import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { useLoadScript } from '@react-google-maps/api';
import { Button } from '@/components/ui';
import { calculateDistances, formatDistance, formatDuration } from '../utils/distanceUtils';

// Static libraries array to prevent performance warnings
const GOOGLE_MAPS_LIBRARIES: ("places" | "geometry")[] = ['places', 'geometry'];

const DistanceDisplayContainer = styled.div`
  background-color: #f8f9fa;
  border: 1px solid #dee2e6;
  border-radius: 8px;
  padding: 16px;
  margin-bottom: 16px;
`;

const DistanceHeader = styled.h6`
  margin-bottom: 12px;
  color: #495057;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const DistanceInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const DistanceRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 0;
  border-bottom: 1px solid #e9ecef;
  
  &:last-child {
    border-bottom: none;
  }
`;

const DistanceLabel = styled.span`
  font-weight: 500;
  color: #495057;
`;

const DistanceValue = styled.span`
  font-weight: 700;
  color: #000000;
`;

const LoadingSpinner = styled.div`
  display: inline-block;
  width: 16px;
  height: 16px;
  border: 2px solid #f3f3f3;
  border-top: 2px solid #0d6efd;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const ErrorMessage = styled.div`
  color: #dc3545;
  font-size: 14px;
  margin-top: 8px;
`;

const NoCoordinatesMessage = styled.div`
  color: #6c757d;
  text-align: center;
  padding: 16px;
  font-style: italic;
`;

// New compact layout components
const CompactDistanceRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 0;
  border-bottom: 1px solid #e9ecef;
  
  &:last-child {
    border-bottom: none;
  }
`;

const CompactDistanceInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const CompactDistanceLabel = styled.span`
  font-weight: 500;
  color: #495057;
`;

const CompactDistanceValue = styled.span`
  font-weight: 700;
  color: #000000;
`;

const CompactButtonGroup = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
`;

interface DistanceDisplayProps {
  startLat: number | null;
  startLng: number | null;
  endLat: number | null;
  endLng: number | null;
  assetLength?: number;
  unitOfMeasure?: string;
  onApplyDistance?: (distance: number) => void;
  disabled?: boolean;
  overrideDistance?: number;
  overrideDuration?: number;
}

const DistanceDisplay: React.FC<DistanceDisplayProps> = ({
  startLat,
  startLng,
  endLat,
  endLng,
  assetLength,
  unitOfMeasure,
  onApplyDistance,
  disabled = false,
  overrideDistance,
  overrideDuration
}) => {
  const [distanceResult, setDistanceResult] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [appliedRoadDistance, setAppliedRoadDistance] = useState<boolean>(false);
  const [appliedAssetLength, setAppliedAssetLength] = useState<boolean>(false);

  const hasValidCoordinates = startLat && startLng && endLat && endLng;
  // Update to recognize all linear measurement units, not just kilometers
  const isDistanceUnit = unitOfMeasure?.toLowerCase().match(/\b(km|m|ft|yd|mi|ln\s*m|linear\s*m|linear\s*ft|linear\s*yd|linear\s*mi)\b/);

  // Ensure Google Maps API is loaded
  const { isLoaded: isGoogleMapsLoaded, loadError: googleMapsLoadError } = useLoadScript({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY || '',
    libraries: GOOGLE_MAPS_LIBRARIES
  });

  useEffect(() => {
    if (overrideDistance != null) {
      setDistanceResult({ roadDistance: overrideDistance, roadDuration: overrideDuration });
      setLoading(false);
      setError(null);
      return;
    }
    if (hasValidCoordinates) {
      // Wait for Google Maps to be loaded before calculating
      if (!isGoogleMapsLoaded && !googleMapsLoadError) {
        // Still loading, wait a bit
        return;
      }

      setLoading(true);
      setError(null);
      
      // If there's a load error, log it but still try to calculate (might use server-side)
      if (googleMapsLoadError) {
        console.warn('Google Maps API load error:', googleMapsLoadError);
      }
      
      calculateDistances(startLat, startLng, endLat, endLng)
        .then(result => {
          setDistanceResult(result);
          setLoading(false);
        })
        .catch(err => {
          console.error('Error calculating distances:', err);
          setError('Failed to calculate distances. Please try again.');
          setLoading(false);
        });
    } else {
      setDistanceResult(null);
      setError(null);
    }
  }, [startLat, startLng, endLat, endLng, hasValidCoordinates, isGoogleMapsLoaded, googleMapsLoadError, overrideDistance, overrideDuration]);
  
  const handleApplyRoadDistance = () => {
    if (distanceResult?.roadDistance && onApplyDistance) {
      const unit = unitOfMeasure?.toLowerCase();
      let distanceToApply = distanceResult.roadDistance;
      
      // For lineal metres, multiply by 1000 when applying to planned accomplishment
      if (unit?.includes('lin m') || unit?.includes('linear m')) {
        distanceToApply = distanceResult.roadDistance * 1000;
      }
      
      onApplyDistance(distanceToApply);
      setAppliedRoadDistance(true);
      // Reset the applied state after 3 seconds
      setTimeout(() => setAppliedRoadDistance(false), 3000);
    }
  };

  const handleApplyAssetLength = () => {
    if (assetLength && onApplyDistance) {
      const unit = unitOfMeasure?.toLowerCase();
      let lengthToApply = assetLength;
      
      // For lineal metres, multiply by 1000 when applying to planned accomplishment
      if (unit?.includes('lin m') || unit?.includes('linear m')) {
        lengthToApply = assetLength * 1000;
      }
      
      onApplyDistance(lengthToApply);
      setAppliedAssetLength(true);
      // Reset the applied state after 3 seconds
      setTimeout(() => setAppliedAssetLength(false), 3000);
    }
  };

  // Helper function to format distance based on unit of measure
  const formatDistanceForDisplay = (distance: number) => {
    if (isDistanceUnit) {
      // For recognized distance units, format appropriately
      const unit = unitOfMeasure?.toLowerCase();
      if (unit?.includes('lin m') || unit?.includes('linear m')) {
        // For lineal metres, display in kilometers but will multiply by 1000 when applying
        return formatDistance(distance);
      } else if (unit?.includes('m') && !unit.includes('km')) {
        // Convert km to meters for meter-based units
        const distanceInMetres = distance * 1000;
        return `${distanceInMetres.toFixed(2)} m`;
      } else if (unit?.includes('ft')) {
        // Convert km to feet
        const distanceInFeet = distance * 3280.84;
        return `${distanceInFeet.toFixed(2)} ft`;
      } else if (unit?.includes('yd')) {
        // Convert km to yards
        const distanceInYards = distance * 1093.61;
        return `${distanceInYards.toFixed(2)} yd`;
      } else if (unit?.includes('mi')) {
        // Convert km to miles
        const distanceInMiles = distance * 0.621371;
        return `${distanceInMiles.toFixed(2)} mi`;
      } else {
        // Default to kilometers
        return formatDistance(distance);
      }
    } else {
      // For unrecognized units, default to kilometers for consistency
      return formatDistance(distance);
    }
  };

  if (!hasValidCoordinates) {
    return (
      <DistanceDisplayContainer>
        <NoCoordinatesMessage>
          <i className="bi bi-geo-alt tw-mr-2"></i>
          Enter both start and end GPS coordinates to calculate road distance
        </NoCoordinatesMessage>
      </DistanceDisplayContainer>
    );
  }

  return (
    <DistanceDisplayContainer>
      <DistanceHeader>
        <i className="bi bi-calculator"></i>
        Road Distance Information
      </DistanceHeader>

      {loading && (
        <div style={{ textAlign: 'center', padding: '16px' }}>
          <LoadingSpinner />
          <span style={{ marginLeft: '8px' }}>Calculating road distance...</span>
        </div>
      )}

      {error && <ErrorMessage>{error}</ErrorMessage>}
      
      {distanceResult?.error && (
        <ErrorMessage>
          <i className="bi bi-exclamation-triangle tw-mr-2"></i>
          {distanceResult.error}
        </ErrorMessage>
      )}

      {distanceResult && !loading && (
        <>
          <DistanceInfo>
            {distanceResult.roadDistance && (
              <CompactDistanceRow>
                <CompactDistanceInfo>
                  <CompactDistanceLabel>Calculated Road Distance based on GPS points:</CompactDistanceLabel>
                  <CompactDistanceValue>
                    {formatDistanceForDisplay(distanceResult.roadDistance)}
                    {distanceResult.error && (
                      <span style={{ marginLeft: '8px', fontSize: '12px', color: '#dc3545' }}>
                        <i className="bi bi-exclamation-triangle"></i>
                      </span>
                    )}
                  </CompactDistanceValue>
                </CompactDistanceInfo>
                {isDistanceUnit && onApplyDistance && (
                  <div className="tw-flex tw-items-center tw-gap-2">
                    {appliedRoadDistance && (
                      <span className="tw-text-success">
                        <i className="bi bi-check-circle-fill"></i>
                      </span>
                    )}
                    <Button
                      type="button"
                      variant="primary"
                      size="sm"
                      onClick={handleApplyRoadDistance}
                      disabled={disabled}
                    >
                      <i className="bi bi-arrow-right-circle tw-mr-1"></i>
                      Apply to Planned Quantity
                    </Button>
                  </div>
                )}
              </CompactDistanceRow>
            )}

            {assetLength && (
              <CompactDistanceRow>
                <CompactDistanceInfo>
                  <CompactDistanceLabel>Asset Length:</CompactDistanceLabel>
                  <CompactDistanceValue>{formatDistanceForDisplay(assetLength)}</CompactDistanceValue>
                </CompactDistanceInfo>
                {isDistanceUnit && onApplyDistance && (
                  <div className="tw-flex tw-items-center tw-gap-2">
                    {appliedAssetLength && (
                      <span className="tw-text-success">
                        <i className="bi bi-check-circle-fill"></i>
                      </span>
                    )}
                    <Button
                      type="button"
                      variant="success"
                      size="sm"
                      onClick={handleApplyAssetLength}
                      disabled={disabled}
                    >
                      <i className="bi bi-arrow-right-circle tw-mr-1"></i>
                      Apply to Planned Quantity
                    </Button>
                  </div>
                )}
              </CompactDistanceRow>
            )}
          </DistanceInfo>
        </>
      )}
    </DistanceDisplayContainer>
  );
};

export default DistanceDisplay; 